import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const DeleteTransaction = ({ userRole, orgId, getToken, onBack, onHome, onEditProfile }) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation('deleteTransaction');
  const { t: tc } = useTranslation('common');

  const [transactions, setTransactions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState('');

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const ALLOWED_ROLES = ['senior_accounting_clerk', 'treasury'];
  const hasAccess = ALLOWED_ROLES.includes(userRole);

  // ── Fetch contributions + expenses in parallel ────────────────────────────
  const fetchTransactions = useCallback(async () => {
    if (!orgId || !hasAccess) return;
    setLoadingData(true);
    setFetchError('');
    try {
      const [contributions, expensesRes] = await Promise.all([
        apiCall(`/api/contributions/?org_id=${orgId}`, {}, getToken),
        apiCall(`/api/expenses/?org_id=${orgId}`, {}, getToken),
      ]);

      // Normalise contributions
      const contribRows = (Array.isArray(contributions) ? contributions : []).map(c => ({
        id: c.id,
        name: c.donor_name || c.donor || 'Unknown Donor',
        type: 'Contribution',
        amount: c.amount,
        date: c.date,
      }));

      // Expenses may come back as { results: [...] } or a plain array
      const expensesArray = Array.isArray(expensesRes)
        ? expensesRes
        : (expensesRes?.results ?? []);

      const expenseRows = expensesArray.map(e => ({
        id: e.id,
        name: e.purpose || 'Unknown Expense',
        type: 'Expense',
        amount: e.amount,
        date: e.date,
        status: e.status,
      }));

      setTransactions([...contribRows, ...expenseRows]);
    } catch (err) {
      setFetchError(err.type === 'NETWORK_ERROR' ? tc('networkError') : (err.detail || tc('error')));
    } finally {
      setLoadingData(false);
    }
  }, [orgId, hasAccess, getToken, tc]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = transactions.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.type.toLowerCase().includes(search.toLowerCase())
  );

  // ── Access guard ──────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#dc2626" />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900 mb-1">{t('accessDenied')}</p>
          <p className="text-sm text-gray-500 mb-6">{t('accessDeniedMessage')}</p>
          <button
            onClick={onBack}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
          >
            {t('goBack')}
          </button>
        </div>
      </div>
    );
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDeleteClick = (transaction) => {
    setConfirmDelete(transaction);
    setConfirmText('');
    setReason('');
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (userRole === 'senior_accounting_clerk') {
      // Senior Clerks must provide a reason and confirmation code
      if (confirmText !== 'DELETE-TXN') {
        setError('Please type "DELETE-TXN" to confirm deletion request');
        return;
      }
    } else {
      // Treasury direct-delete requires confirmation code
      if (confirmText !== 'DELETE-TXN') {
        setError('Please type "DELETE-TXN" to confirm deletion');
        return;
      }
    }

    setLoading(true);
    try {
      if (userRole === 'senior_accounting_clerk') {
        // Senior Clerks must request deletion instead of deleting directly
        const url = confirmDelete.type === 'Contribution'
          ? `/api/contributions/${confirmDelete.id}/deletion-requests/`
          : `/api/expenses/${confirmDelete.id}/deletion-requests/`;
        
        await apiCall(
          url,
          { method: 'POST', body: { reason: reason || 'Requested via UI' } },
          getToken
        );
        
        setTransactions(prev => prev.filter(t => !(t.id === confirmDelete.id && t.type === confirmDelete.type)));
      } else {
        // Treasury can still direct-delete via the old endpoints, which check for CanApproveDeletion
        const url = confirmDelete.type === 'Contribution'
          ? `/api/contributions/${confirmDelete.id}/`
          : `/api/expenses/${confirmDelete.id}/`;
          
        await apiCall(
          url,
          { method: 'DELETE' },
          getToken,
        );
        
        setTransactions(prev => prev.filter(t => !(t.id === confirmDelete.id && t.type === confirmDelete.type)));
      }

      setConfirmDelete(null);
      setDeleted(true);
      setTimeout(() => setDeleted(false), 3000);
    } catch (err) {
      setError(err.type === 'NETWORK_ERROR' ? tc('networkError') : (err.detail || err.message || 'Failed to request/delete transaction'));
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>
        <button onClick={onBack} aria-label="Back" title="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Success message */}
        {deleted && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-green-700">
              {userRole === 'senior_accounting_clerk'
                ? t('requestSubmittedMessage')
                : t('successMessage')}
            </p>
          </div>
        )}

        {/* Fetch error */}
        {fetchError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{fetchError}</p>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{tc('search')}</p>
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />
        </div>

        {/* Transactions Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_40px] gap-0 border-b border-gray-200 px-3 py-2.5 bg-gray-50">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{t('tableHeader.name')}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{t('tableHeader.type')}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{t('tableHeader.amount')}</div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{t('tableHeader.date')}</div>
            <div></div>
          </div>

          {/* Table Rows */}
          {loadingData ? (
            <div className="p-6 text-center text-sm text-gray-500">{t('loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="p-5 text-center text-sm text-gray-500">{t('noData')}</div>
          ) : (
            filtered.map(txn => (
              <div
                key={`${txn.type}-${txn.id}`}
                data-testid={`transaction-${txn.type}-${txn.id}`}
                className="grid grid-cols-[1.5fr_1fr_1fr_1fr_40px] gap-0 border-b border-gray-200 px-3 py-2.5 items-center text-xs"
              >
                <div className="text-gray-900 font-medium truncate">{txn.name}</div>
                <div>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      txn.type === 'Contribution' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {txn.type === 'Contribution' ? t('contribution') : t('expense')}
                  </span>
                </div>
                <div className="text-gray-900 font-medium">₹{Number(txn.amount).toFixed(2)}</div>
                <div className="text-gray-500">{txn.date}</div>
                <button
                  onClick={() => handleDeleteClick(txn)}
                  title="Delete"
                  className="bg-red-50 text-red-600 border border-red-200 w-8 h-8 rounded-md flex items-center justify-center hover:bg-red-100 transition"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete / Request Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title" className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs shadow-xl">
            <p id="delete-confirm-title" className="text-sm font-medium text-gray-900 mb-3">
              {userRole === 'senior_accounting_clerk' ? t('requestConfirmTitle') : t('confirmTitle')}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              {userRole === 'senior_accounting_clerk' ? t('requestConfirmInstructions') : t('confirmInstructions')}{' '}
              <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">DELETE-TXN</span>{' '}
              {userRole === 'senior_accounting_clerk' ? t('requestConfirmCode') : t('confirmCode')}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 mb-3">
              <p className="text-xs text-gray-700">
                <strong>{confirmDelete.name}</strong>
                <span
                  className={`ml-2 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                    confirmDelete.type === 'Contribution' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {confirmDelete.type}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-1">₹{Number(confirmDelete.amount).toFixed(2)} on {confirmDelete.date}</p>
            </div>

            {error && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {userRole === 'senior_accounting_clerk' && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1.5">{t('reasonLabel')}</p>
                <textarea
                  placeholder={t('reasonPlaceholder')}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0 resize-none"
                />
              </div>
            )}

            <input
              type="text"
              placeholder={t('confirmPlaceholder')}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0 mb-4"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={loading}
                className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-60"
              >
                {tc('cancel')}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading || confirmText !== 'DELETE-TXN'}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {loading ? t('deleting') : (userRole === 'senior_accounting_clerk' ? t('submitRequest') : t('confirmDelete'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteTransaction;